// src/components/noticeboard/Noticeboard.jsx
//
// The noticeboard: a megaphone in the header, and behind it a short list of
// advisory notices — planned maintenance, incidents, product updates
// (Noticeboard contract v1 §6, backend-backed v1.1; design
// "MaintenanceNotice", 3 Oct 2026).
//
//   - live notices exist  → the icon is highlighted and carries the TOTAL count
//   - some are unseen     → the badge turns accent and the board opens itself,
//                           once per page load
//   - closing the board (Got it, ✕, Esc, outside click) acknowledges every
//     notice listed
//
// It never blocks anything. The maintenance flag is the barrier; this is the
// banner. It is shared by the suite pages and the Kuestion app, so it takes
// its zone and clock as props rather than reaching into either app.
//
// Props:
//   surface         'suite' | 'kuestion'   which feed to read
//   store           'rw' | 'kq'            which acknowledgement key to use
//   size            px                     36 on suite pages, 34 in the app
//   autoOpen        bool                   a host holds this false during a tour
//   zone, clock     effective zone id and '12' | '24'; device zone when absent
//   refetchOnFocus  bool                   in the app: refetch when stale
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadNotices, cachedNotices, isStale, readSeen, writeSeen, tokenOf, claimAutoOpen } from './noticeboardApi';
import './Noticeboard.css';

const KIND = {
  maintenance: { label: 'Maintenance', tone: 'accent' },
  incident: { label: 'Incident', tone: 'accent' },
  info: { label: 'Update', tone: 'neutral' },
};

const deviceZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

// `date` is the calendar date the notice is about: the 11th everywhere.
const dateLabel = (ymd) => {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${ymd}T00:00:00Z`));
  } catch { return ymd; }
};

// The window is an instant range: rendered in the reader's zone, and the zone
// is named. Both dates are shown when it crosses midnight locally.
export const windowLabel = (from, to, zone, clock) => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  const render = (z) => {
    const d = (x) => new Intl.DateTimeFormat('en-GB', { timeZone: z, day: 'numeric', month: 'short' }).format(x);
    const t = (x) => new Intl.DateTimeFormat('en-GB', { timeZone: z, hour: '2-digit', minute: '2-digit', hour12: clock === '12' }).format(x);
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: z, timeZoneName: 'shortOffset' }).formatToParts(a);
    const off = (p.find((x) => x.type === 'timeZoneName') || {}).value || z;
    return `${d(a)}, ${t(a)}–${d(a) === d(b) ? '' : `${d(b)}, `}${t(b)} (${off})`;
  };
  try { return render(zone || deviceZone()); } catch { try { return render('UTC'); } catch { return ''; } }
};

export default function Noticeboard({ surface = 'suite', store = 'rw', size = 36, autoOpen = true, zone, clock, refetchOnFocus = false }) {
  const [notices, setNotices] = useState(() => cachedNotices(surface) || []);
  const [seen, setSeen] = useState(() => readSeen(store));
  const [open, setOpen] = useState(false);

  // One read on mount; a cached answer younger than ten minutes is reused.
  useEffect(() => {
    let live = true;
    setNotices(cachedNotices(surface) || []);
    loadNotices(surface).then((n) => { if (live) setNotices(n); });
    return () => { live = false; };
  }, [surface]);

  // In the app: no polling, but coming back to the tab after ten minutes rereads.
  useEffect(() => {
    if (!refetchOnFocus) return undefined;
    let live = true;
    const onFocus = () => { if (isStale(surface)) loadNotices(surface, { force: true }).then((n) => { if (live) setNotices(n); }); };
    window.addEventListener('focus', onFocus);
    return () => { live = false; window.removeEventListener('focus', onFocus); };
  }, [surface, refetchOnFocus]);

  useEffect(() => { setSeen(readSeen(store)); }, [store]);

  const fresh = useMemo(() => notices.filter((n) => seen.indexOf(tokenOf(n)) === -1), [notices, seen]);
  const live = notices.length > 0;

  // Opens by itself when anything is unseen — once per page load, and only
  // when the host is not holding it back (a tour, a section intro).
  useEffect(() => {
    if (autoOpen && fresh.length > 0 && claimAutoOpen()) setOpen(true);
  }, [autoOpen, fresh.length]);

  // Every close acknowledges everything listed.
  const close = useCallback(() => {
    setSeen(writeSeen(store, notices));
    setOpen(false);
  }, [store, notices]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const plural = (n) => `${n} notice${n === 1 ? '' : 's'}`;
  const title = !live ? 'Noticeboard' : fresh.length ? `Noticeboard — ${fresh.length} new notice${fresh.length === 1 ? '' : 's'}` : `Noticeboard — ${plural(notices.length)}`;
  const countLine = !live ? 'Nothing to report' : `${plural(notices.length)}${fresh.length ? ` · ${fresh.length} new` : ''}`;

  return (
    <div className="rw-nb">
      <button
        type="button"
        className={`rw-nb__btn${live ? ' is-live' : ''}${open ? ' is-open' : ''}`}
        style={{ width: size, height: size }}
        onClick={() => (open ? close() : setOpen(true))}
        title={title}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></svg>
        {live && <span className={`rw-nb__badge${fresh.length ? ' is-new' : ''}`} aria-hidden="true">{notices.length > 9 ? '9+' : notices.length}</span>}
      </button>

      {open && (
        <>
          <div className="rw-nb__backdrop" onClick={close} aria-hidden="true" />
          <div className="rw-nb__board" role="dialog" aria-label="Noticeboard">
            <div className="rw-nb__rule" />
            <div className="rw-nb__head">
              <span style={{ minWidth: 0 }}>
                <span className="rw-nb__title">Noticeboard</span>
                <span className="rw-nb__count">{countLine}</span>
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" className="rw-nb__x" onClick={close} title="Minimise" aria-label="Minimise the noticeboard">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
              </button>
            </div>

            <div className="rw-nb__list">
              {notices.map((n) => {
                const k = KIND[n.kind] || KIND.info;
                const isNew = fresh.indexOf(n) !== -1;
                const win = n.affects_from && n.affects_to ? windowLabel(n.affects_from, n.affects_to, zone, clock) : '';
                return (
                  <article key={tokenOf(n)} className={`rw-nb__item${isNew ? ' is-new' : ''}`}>
                    <div className="rw-nb__meta">
                      <span className={`rw-nb__tag is-${k.tone}`}>{k.label}</span>
                      <span className="rw-nb__date">{dateLabel(n.date)}</span>
                      {isNew && <span className="rw-nb__new">New</span>}
                    </div>
                    <h3>{n.heading}</h3>
                    {/* Text, never HTML: the board is on public login pages. */}
                    <p>{n.body}</p>
                    {win && (
                      <div className="rw-nb__window">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></svg>
                        {win}
                      </div>
                    )}
                  </article>
                );
              })}
              {!live && <div className="rw-nb__empty">No notices right now.</div>}
            </div>

            <div className="rw-nb__foot">
              <button type="button" className="rw-nb__ok" onClick={close}>Got it</button>
              <span>Read these again any time from the megaphone in the header.</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
